import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  FileText,
  Phone,
  Calendar,
  CheckCircle,
  ArrowRight,
  Clock,
  User,
  ClipboardList,
  Home,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const STEPS = [
  {
    number: 1,
    title: "Kontakta Nacka kommun",
    icon: Phone,
    description:
      "Ring servicecenter eller besök nacka.se för att göra din ansökan.",
    details: [
      "Ring 08-718 80 00 (vardagar 08:00-17:00)",
      "Besök nacka.se/omsorg-stod/senior/hjalp-i-hemmet/hemtjanst/",
      "Du kan också besöka stadshuset personligen",
    ],
    tip: "Ha personnummer och telefonnummer redo när du ringer.",
  },
  {
    number: 2,
    title: "Boka möte med biståndshandläggare",
    icon: Calendar,
    description: "En handläggare kontaktar dig för att boka ett möte.",
    details: [
      "Mötet sker oftast hemma hos dig",
      "Du får berätta om din situation och dina behov",
      "Ta gärna med en anhörig som stöd",
    ],
    tip: "Skriv ner dina frågor i förväg så du inte glömmer något.",
  },
  {
    number: 3,
    title: "Utredning och bedömning",
    icon: ClipboardList,
    description: "Handläggaren utreder dina behov och fattar ett beslut.",
    details: [
      "Handläggaren bedömer vilken hjälp du behöver",
      "Beslutet baseras på socialtjänstlagen",
      "Du får ett skriftligt beslut hemskickat",
    ],
    tip: "Du har rätt att överklaga om du inte är nöjd med beslutet.",
  },
  {
    number: 4,
    title: "Välj din anordnare",
    icon: User,
    description:
      "När du fått beslut om hemtjänst väljer du vilken anordnare du vill ha.",
    details: [
      "Du får en lista över godkända anordnare",
      "Du kan välja fritt bland alla på listan",
      "Utföraren kontaktar dig för att planera starten",
    ],
    tip: "Läs vår guide om hur du väljer rätt anordnare.",
  },
  {
    number: 5,
    title: "Hemtjänsten startar",
    icon: Home,
    description:
      "Din valda anordnare kommer hem till dig och ni planerar tillsammans.",
    details: [
      "Första mötet handlar om att lära känna varandra",
      "Ni går igenom vad du behöver hjälp med",
      "Ett schema sätts upp efter dina önskemål",
    ],
    tip: "Du kan byta anordnare när som helst om du inte är nöjd.",
  },
];

const WHAT_YOU_NEED = [
  "Personnummer",
  "Telefonnummer där vi kan nå dig",
  "Eventuella läkarintyg eller dokumentation om hälsotillstånd",
  "Information om din nuvarande boendesituation",
  "Lista på vilken typ av hjälp du önskar",
];

const ANSOKA_FAQ_ITEMS = [
  {
    question: "Hur ansöker jag om hemtjänst i Nacka?",
    answer:
      "Du ansöker genom att kontakta Nacka kommuns biståndshandläggare. Ring 08-718 80 00 eller besök Nacka kommuns hemsida. En handläggare kommer att bedöma dina behov och besluta om vilken hjälp du har rätt till.",
  },
  {
    question: "Hur lång tid tar det att få hemtjänst?",
    answer:
      "I akuta fall kan hjälpen starta redan samma dag. Normalt tar det några dagar till ett par veckor från ansökan till att hjälpen börjar, beroende på hur snabbt utredningen kan göras.",
  },
];

/**
 * Ansoka - Step-by-step guide to applying for home care in Nacka
 *
 * SEO Focus: "ansöka hemtjänst Nacka", "få hemtjänst Nacka"
 * Migrated and enhanced from original WordPress content
 */
const Ansoka: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Ansök om hemtjänst i Nacka | Steg-för-steg guide",
        description:
          "Komplett guide för att ansöka om hemtjänst i Nacka kommun. Från första kontakt till att hjälpen startar - vi går igenom varje steg.",
        url: "https://nackahemtjanst.se/ansoka",
        datePublished: "2025-12-22",
        dateModified: "2026-01-09",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        ANSOKA_FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/ansoka",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Ansök",
          url: "https://nackahemtjanst.se/ansoka",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, faqSchema, breadcrumbSchema]),
    [articleSchema, faqSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Ansök om hemtjänst i Nacka | Steg-för-steg guide"
        description="Komplett guide för att ansöka om hemtjänst i Nacka kommun. Från första kontakt till att hjälpen startar - vi går igenom varje steg."
        canonicalUrl="https://nackahemtjanst.se/ansoka"
        ogType="article"
        keywords={[
          "ansöka hemtjänst Nacka",
          "få hemtjänst Nacka",
          "ansökan hemtjänst Nacka",
          "hemtjänst Nacka ansökan",
          "biståndshandläggare Nacka",
        ]}
        contentClassification="Consumer Guide"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-09T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero with Glass Effect */}
      <section className="relative bg-gradient-to-b from-blue-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-transparent to-emerald-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <FileText className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Ansök om hemtjänst i Nacka
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            Att ansöka om hemtjänst kan kännas överväldigande. Här går vi igenom
            processen steg för steg så att du vet exakt vad du kan förvänta dig.
          </p>
        </div>
      </section>

      {/* Quick Contact with Glass Effect */}
      <section className="py-8 px-4 bg-gradient-to-br from-emerald-700 to-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700/95 to-emerald-600/95 backdrop-blur-sm" />
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">
          <div className="flex items-center gap-3">
            <Phone className="w-6 h-6" />
            <span className="text-lg">Vill du prata med någon direkt?</span>
          </div>
          <a
            href="tel:0871880000"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-emerald-700 font-bold hover:bg-emerald-50 transition-colors shadow-lg"
          >
            Ring 08-718 80 00
          </a>
        </div>
      </section>

      {/* What You Need with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <h2 className="text-2xl font-bold text-amber-900 mb-4">
              Förbered dig – detta behöver du ha redo
            </h2>
            <ul className="space-y-3">
              {WHAT_YOU_NEED.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-900">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Step by Step with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-12 text-center">
            Så går ansökan till – 5 steg
          </h2>

          <div className="space-y-8">
            {STEPS.map((step, index) => (
              <div
                key={step.number}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-6 md:p-8 hover:shadow-glass-hover transition-all"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-blue-600 text-white flex items-center justify-center">
                      <span className="text-2xl font-bold">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <step.icon className="w-6 h-6 text-blue-600" />
                      <h3 className="text-2xl font-bold text-surface-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-lg text-surface-600 mb-4">
                      {step.description}
                    </p>

                    <ul className="space-y-2 mb-4">
                      {step.details.map((detail, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-surface-700"
                        >
                          <span className="text-blue-600 mt-1">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>

                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Tips:</strong> {step.tip}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < STEPS.length - 1 && (
                  <div className="flex justify-center mt-6">
                    <div className="w-0.5 h-8 bg-blue-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Info with Glass Effect */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <div className="flex items-start gap-4">
              <Clock className="w-8 h-8 text-surface-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-surface-900 mb-4">
                  Hur lång tid tar det?
                </h2>
                <div className="space-y-4 text-surface-700">
                  <p>
                    <strong>Akuta fall:</strong> Om du har ett akut behov kan
                    hemtjänsten starta redan samma dag eller dagen efter.
                  </p>
                  <p>
                    <strong>Normala ärenden:</strong> Från ansökan till att
                    hemtjänsten startar tar det vanligtvis 1-3 veckor, beroende
                    på hur snabbt utredningen kan göras.
                  </p>
                  <p>
                    <strong>Skriftligt beslut:</strong> Kommunen ska fatta
                    beslut inom rimlig tid. Du har rätt att få veta ungefär hur
                    lång tid det kommer att ta.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Step CTA with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Fått beslut om hemtjänst?
            </h2>
            <p className="text-xl text-surface-600 mb-8 max-w-2xl mx-auto">
              Nästa steg är att välja vilken anordnare du vill ha. Vi hjälper
              dig att jämföra och hitta rätt.
            </p>
            <Link
              to={getPath("/hitta-hemtjanst")}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold shadow-lg hover:bg-emerald-700 transition-all"
            >
              Hitta rätt anordnare
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För officiell
              information och ansökan, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>{" "}
              direkt.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Letar du efter information om hemtjänst i andra kommuner? På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              hittar du information om hemtjänst i alla Sveriges kommuner.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Ansoka;
